import { html, nothing } from "lit";

import type { AppViewState } from "./app-view-state";
import { parseAgentSessionKey } from "../lib/session-key.js";
import {
  TAB_GROUPS,
  iconForTab,
  titleForTab,
  type Tab,
} from "./navigation";
import { icons } from "./icons";
import { refreshChatAvatar } from "./app-chat";
import { renderChat } from "./views/chat";
import { renderCron } from "./views/cron";
import { renderExecApprovalPrompt } from "./views/exec-approval";
import { renderMcpSetupPrompt } from "./views/mcp-setup-prompt";
import { updateMcpSetupInput, submitMcpSetup, cancelMcpSetup } from "./controllers/mcp-setup";
import { renderGatewayUrlConfirmation } from "./views/gateway-url-confirmation";
import { renderTokenPrompt } from "./views/token-prompt";
import { renderFiles } from "./views/files";
import { loadFiles, downloadFile, deleteFile } from "./controllers/files";
import { renderExplore } from "./views/explore";
// Security tab removed — Sontara users manage security from dashboard Settings page
import { renderChatControls, renderTab, renderThemeToggle } from "./app-render.helpers";
import { loadSkills } from "./controllers/skills";
import {
  loadMcpServers,
  loadMcpCatalog,
  connectMcpServer,
  disconnectMcpServer,
  updateMcpEnvInput,
} from "./controllers/mcp";
import { loadChatHistory, uploadDocument } from "./controllers/chat";
import type { ChatAttachment } from "./ui-types";
import { loadCronRuns, toggleCronJob, runCronJob, removeCronJob, addCronJob } from "./controllers/cron";

const AVATAR_DATA_RE = /^data:/i;
const AVATAR_HTTP_RE = /^https?:\/\//i;

function resolveAssistantAvatarUrl(state: AppViewState): string | undefined {
  const list = state.agentsList?.agents ?? [];
  const parsed = parseAgentSessionKey(state.sessionKey);
  const agentId =
    parsed?.agentId ??
    state.agentsList?.defaultId ??
    "main";
  const agent = list.find((entry) => entry.id === agentId);
  const identity = agent?.identity;
  const candidate = identity?.avatarUrl ?? identity?.avatar;
  if (!candidate) return undefined;
  if (AVATAR_DATA_RE.test(candidate) || AVATAR_HTTP_RE.test(candidate)) return candidate;
  return identity?.avatarUrl;
}

const IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
]);

function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function handleFileUpload(state: AppViewState, files: FileList) {
  const baseUrl = (state.settings.gatewayUrl?.replace(/^ws/, "http") ?? "").replace(/\/ws\/?$/, "");
  const token = state.settings.token ?? "";

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (IMAGE_TYPES.has(file.type)) {
      // Images: convert to base64 inline attachment (same as paste)
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const att: ChatAttachment = {
          id: generateAttachmentId(),
          dataUrl,
          mimeType: file.type,
          fileName: file.name,
          fileSize: file.size,
        };
        state.chatAttachments = [...state.chatAttachments, att];
      };
      reader.readAsDataURL(file);
    } else {
      // Documents: upload to server
      state.uploading = true;
      void uploadDocument(baseUrl, token, file).then((result) => {
        state.uploading = false;
        if (result) {
          const att: ChatAttachment = {
            id: generateAttachmentId(),
            dataUrl: "",
            mimeType: result.mime_type,
            fileName: result.filename,
            fileSize: result.size,
            isDocument: true,
          };
          state.chatAttachments = [...state.chatAttachments, att];
        }
      });
    }
  }
}

export function renderApp(state: AppViewState) {
  const chatDisabledReason = state.connected
    ? null
    : state.reconnecting
      ? "Reconnecting to your agent..."
      : "Disconnected from gateway.";
  const isChat = state.tab === "chat";
  const chatFocus = isChat && (state.settings.chatFocusMode || state.onboarding);
  const showThinking = state.onboarding ? false : state.settings.chatShowThinking;
  const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
  const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;

  return html`
    <div class="shell ${isChat ? "shell--chat" : ""} ${chatFocus ? "shell--chat-focus" : ""} ${state.settings.navCollapsed ? "shell--nav-collapsed" : ""} ${state.onboarding ? "shell--onboarding" : ""}">
      <header class="topbar">
        <div class="topbar-left">
          <button
            class="nav-collapse-toggle"
            @click=${() =>
              state.applySettings({
                ...state.settings,
                navCollapsed: !state.settings.navCollapsed,
              })}
            title="${state.settings.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}"
            aria-label="${state.settings.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}"
          >
            <span class="nav-collapse-toggle__icon">${icons.menu}</span>
          </button>
          <div class="brand">
            <div class="brand-logo">
              <svg viewBox="0 0 32 32" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="14" stroke="currentColor" stroke-width="2"/>
                <circle cx="11" cy="13" r="2" fill="currentColor"/>
                <circle cx="21" cy="13" r="2" fill="currentColor"/>
                <path d="M10 20c1.5 2.5 4 4 6 4s4.5-1.5 6-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="16" cy="1" r="1" fill="currentColor"/>
              </svg>
            </div>
            <div class="brand-text">
              <div class="brand-title">Sontara</div>
            </div>
          </div>
        </div>
        <div class="topbar-status">
          <div class="pill${state.reconnecting ? " warning" : ""}">
            <span class="statusDot ${state.connected ? "ok" : state.reconnecting ? "reconnecting" : ""}"></span>
            <span>${state.connected ? "Online" : state.reconnecting ? "Reconnecting" : "Offline"}</span>
          </div>
          ${renderThemeToggle(state)}
        </div>
      </header>
      <aside class="nav ${state.settings.navCollapsed ? "nav--collapsed" : ""}">
        ${TAB_GROUPS.flatMap((group) =>
          group.tabs.map((tab) => renderTab(state, tab))
        )}
      </aside>
      <main class="content ${isChat ? "content--chat" : ""}">
        <section class="content-header">
          <div>
            <div class="page-title">${titleForTab(state.tab)}</div>
          </div>
          <div class="page-meta">
            ${state.reconnecting
              ? html`<div class="pill warning">Reconnecting...</div>`
              : state.lastError
                ? html`<div class="pill danger">${state.lastError}</div>`
                : nothing}
            ${isChat ? renderChatControls(state) : nothing}
          </div>
        </section>

        ${state.tab === "chat"
          ? renderChat({
              sessionKey: state.sessionKey,
              onSessionKeyChange: (next) => {
                state.sessionKey = next;
                state.chatMessage = "";
                state.chatAttachments = [];
                state.chatStream = null;
                state.chatStreamStartedAt = null;
                state.chatRunId = null;
                state.chatQueue = [];
                state.resetToolStream();
                state.resetChatScroll();
                state.applySettings({
                  ...state.settings,
                  sessionKey: next,
                  lastActiveSessionKey: next,
                });
                void state.loadAssistantIdentity();
                void loadChatHistory(state);
                void refreshChatAvatar(state);
              },
              thinkingLevel: state.chatThinkingLevel,
              showThinking,
              loading: state.chatLoading,
              sending: state.chatSending,
              compactionStatus: state.compactionStatus,
              assistantAvatarUrl: chatAvatarUrl,
              messages: state.chatMessages,
              toolMessages: state.chatToolMessages,
              stream: state.chatStream,
              streamStartedAt: state.chatStreamStartedAt,
              draft: state.chatMessage,
              queue: state.chatQueue,
              connected: state.connected,
              canSend: state.connected,
              disabledReason: chatDisabledReason,
              error: state.lastError,
              sessions: state.sessionsResult,
              focusMode: chatFocus,
              onRefresh: () => {
                state.resetToolStream();
                return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
              },
              onToggleFocusMode: () => {
                if (state.onboarding) return;
                state.applySettings({
                  ...state.settings,
                  chatFocusMode: !state.settings.chatFocusMode,
                });
              },
              onChatScroll: (event) => state.handleChatScroll(event),
              onDraftChange: (next) => (state.chatMessage = next),
              attachments: state.chatAttachments,
              onAttachmentsChange: (next) => (state.chatAttachments = next),
              uploading: state.uploading,
              onFileUpload: (files) => handleFileUpload(state, files),
              onSend: () => state.handleSendChat(),
              canAbort: Boolean(state.chatRunId),
              onAbort: () => void state.handleAbortChat(),
              onQueueRemove: (id) => state.removeQueuedMessage(id),
              onNewSession: () =>
                state.handleSendChat("/new", { restoreDraft: true }),
              sidebarOpen: state.sidebarOpen,
              sidebarContent: state.sidebarContent,
              sidebarError: state.sidebarError,
              splitRatio: state.splitRatio,
              onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
              onCloseSidebar: () => state.handleCloseSidebar(),
              onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
              assistantName: state.assistantName,
              assistantAvatar: state.assistantAvatar,
              voiceState: state.voiceController ? state.voiceState : undefined,
              voiceTranscription: state.voiceController ? state.voiceTranscription : undefined,
              onVoiceToggle: state.voiceController ? () => {
                const vc = state.voiceController;
                if (!vc) return;
                if (state.voiceState === "idle") {
                  void vc.start();
                } else {
                  void vc.stop();
                }
              } : undefined,
            })
          : nothing}

        ${state.tab === "cron"
          ? renderCron({
              loading: state.cronLoading,
              status: state.cronStatus,
              jobs: state.cronJobs,
              error: state.cronError,
              busy: state.cronBusy,
              form: state.cronForm,
              channels: state.channelsSnapshot?.channelMeta?.length
                ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
                : state.channelsSnapshot?.channelOrder ?? [],
              channelLabels: state.channelsSnapshot?.channelLabels ?? {},
              channelMeta: state.channelsSnapshot?.channelMeta ?? [],
              runsJobId: state.cronRunsJobId,
              runs: state.cronRuns,
              onFormChange: (patch) => (state.cronForm = { ...state.cronForm, ...patch }),
              onRefresh: () => state.loadCron(),
              onAdd: () => addCronJob(state),
              onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
              onRun: (job) => runCronJob(state, job),
              onRemove: (job) => removeCronJob(state, job),
              onLoadRuns: (jobId) => loadCronRuns(state, jobId),
            })
          : nothing}

        ${state.tab === "files"
          ? renderFiles({
              loading: state.filesLoading,
              files: state.filesList,
              error: state.filesError,
              onRefresh: () => loadFiles(state),
              onDownload: (filename) => downloadFile(state, filename),
              onDelete: (filename) => deleteFile(state, filename),
            })
          : nothing}

        ${state.tab === "capabilities"
          ? renderExplore({
              subTab: state.exploreSubTab,
              onSubTabChange: (next) => {
                state.exploreSubTab = next;
                if (next === "mcp") {
                  loadMcpCatalog(state);
                  loadMcpServers(state);
                }
              },
              capabilities: {
                filter: state.capabilitiesFilter ?? "",
                categoryFilter: state.capabilitiesCategoryFilter ?? "",
                onFilterChange: (next) => (state.capabilitiesFilter = next),
                onCategoryFilterChange: (next) => (state.capabilitiesCategoryFilter = next),
                onUsePrompt: (prompt) => {
                  state.chatMessage = prompt;
                  state.setTab("chat");
                },
              },
              mcp: {
                servers: state.mcpServers ?? [],
                catalog: state.mcpCatalog ?? [],
                loading: state.mcpLoading ?? false,
                error: state.mcpError ?? null,
                busyServer: state.mcpBusyServer ?? null,
                envInputs: state.mcpEnvInputs ?? {},
                messages: state.mcpMessages ?? {},
                onConnect: (name) => connectMcpServer(state, name),
                onDisconnect: (name) => disconnectMcpServer(state, name),
                onEnvInput: (server, env, val) => updateMcpEnvInput(state, server, env, val),
                onRefresh: () => { loadMcpCatalog(state); loadMcpServers(state); },
              },
            })
          : nothing}


      </main>
      ${renderExecApprovalPrompt(state)}
      ${renderMcpSetupPrompt({
        queue: state.mcpSetupQueue ?? [],
        inputs: state.mcpSetupInputs ?? {},
        busy: state.mcpSetupBusy ?? null,
        onInput: (id, env, val) => updateMcpSetupInput(state, id, env, val),
        onSubmit: (req) => submitMcpSetup(state, req),
        onCancel: (req) => cancelMcpSetup(state, req),
      })}
      ${renderGatewayUrlConfirmation(state)}
      ${renderTokenPrompt({
        visible: state.showTokenPrompt,
        reason: state.tokenPromptReason ?? "insecure-no-token",
        tokenValue: state.tokenPromptValue,
        lastError: state.lastError,
        onTokenChange: (v) => (state.tokenPromptValue = v),
        onSubmit: () => state.handleTokenSubmit(),
        onDismiss: () => state.handleTokenDismiss(),
      })}
    </div>
  `;
}
